import TextField from '@mui/material/TextField';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { z } from 'zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import FormDialogContainer from '../../../FormDialogContainer/FormDialogContainer';
import { DialogActionButtons } from '../../../DialogActionButtons/DialogActionButtons';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
} from '@mui/material';
import { createJobSchema } from '../../../../Utils/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkJobReferenceExists, createJob, CreateJobData, getLastJobReference } from '../../../../Services/Jobs';
import { useEffect, useState } from 'react';
import { getClients } from '../../../../Services/Client';
import ClientFormDialog from './ClientFormDialog';
import { Loader } from '../../../Loader/Loader';
import { getJobDepartments } from '../../../../Services/jobDepartment';

interface JobFormDialogProps {
  open: boolean;
  handleClose: () => void;
  onInvalidateQuery: () => void;
}

export default function JobFormDialog({
  open,
  handleClose,
  onInvalidateQuery,
}: JobFormDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isConfidential, setIsConfidential] = useState(false);
  const [openAddClientModal, setOpenAddClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<JobDepartment | null>(null);
  const [selectedJobReference, setSelectedJobReference] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const nameLabel = t('form_fields.name.job_label');
  const headcountLabel = t('form_fields.headcount.label');
  const descriptionLabel = t('form_fields.description.label');
  const seniorityLabel = t('form_fields.seniority.label');
  const seniorityAdvancedLabel = t('form_fields.seniority.options.advanced');
  const seniorityAdvancedPlusLabel = t('form_fields.seniority.options.advanced_plus');
  const seniorityJuniorLabel = t('form_fields.seniority.options.junior');
  const senioritySeniorLabel = t('form_fields.seniority.options.senior');
  const createJobTitle = t('jobs.create.title');
  const createJobDescription = t('jobs.create.description');
  const toastErrorMessage = t('toast.errors.something_went_wrong');
  const toastSuccessMessage = t('toast.success.create', { elem: 'Job' });

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    register,
    reset,
    control,
    trigger
  } = useForm<z.infer<typeof createJobSchema>>({
    resolver: zodResolver(createJobSchema)
  });

  const { mutate: createJobMutation } = useMutation({
    mutationFn: (data: CreateJobData) => createJob(data),
    onMutate: () => {
      setIsLoading(true);
    },
    onError: (error: any) => {
      toast.error(toastErrorMessage);
      console.error({ error });
      setIsLoading(false);
    },
    onSuccess: () => {
      toast.success(toastSuccessMessage);
      handleModalClose();
      onInvalidateQuery();
      setIsLoading(false);
      queryClient.invalidateQueries({ queryKey: ['getJobReferences'] });
    },
  });

  const onSubmit: SubmitHandler<z.infer<typeof createJobSchema>> = async (data) => {
    if (data.jobReference) {
      try {
        const exists = await checkJobReferenceExists(data.jobReference);
        if (exists) {
          toast.error(t('form_fields.errors.job_reference_already_exists'));
          return;
        }
      } catch (error) {
        toast.error(toastErrorMessage);
        console.error('Error validating job reference', error);
        return;
      }
    }

    createJobMutation({ ...data, confidential: isConfidential });
  };

  const handleChange = (event: SelectChangeEvent) => {
    setValue('seniority', event.target.value);
  };

  const { data: clients = [], isLoading: isLoadingClients } = useQuery<Client[]>({
    queryKey: ['getClients'],
    queryFn: async () => {
      const response = await getClients({
        page: 1,
        count: 1000,
      });
      return response.items;
    },
  });

  const { data: jobDepartmentsResponse, isLoading: isLoadingJobDepartments } = useQuery({
    queryKey: ['getJobDepartments'],
    queryFn: () => getJobDepartments(),
  });

  const { data: jobReferencesResponse, isLoading: isLoadingJobReferences } = useQuery({
    queryKey: ['getJobReferences'],
    queryFn: () => getLastJobReference(),
  });

  useEffect(() => {
    setIsLoading(isLoadingClients || isLoadingJobDepartments || isLoadingJobReferences);
  }, [isLoadingClients, isLoadingJobDepartments, isLoadingJobReferences]);
  useEffect(() => {
    if (jobReferencesResponse) {
      setValue('jobReference', jobReferencesResponse);
      setSelectedJobReference(jobReferencesResponse);
    }
  }, [jobReferencesResponse, setValue]);

  const handleAddNewClient = (newClient: Client) => {
    queryClient.invalidateQueries({ queryKey: ['getClients'] });
    setSelectedClient(newClient);
    if (newClient.id) {
      setValue('clientId', newClient.id);
    }
    setOpenAddClientModal(false);
  };

  const handleModalClose = () => {
    handleClose();
    reset();
    setSelectedClient(null);
    setSelectedDepartment(null);
    setSelectedJobReference(null);
  };

  return (
    <FormDialogContainer open={open} handleClose={handleModalClose} title={createJobTitle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ paddingTop: 0 }}>
          <DialogContentText>{createJobDescription}</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="position"
            label={nameLabel}
            type="text"
            fullWidth
            variant="outlined"
            {...register('position')}
            error={!!errors.position}
            helperText={errors.position?.message}
          />
          <Box display="flex" alignItems="center">
            <Controller
              name="clientId"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  sx={{ marginTop: 1 }}
                  id="client-autocomplete"
                  options={Array.isArray(clients) ? clients : []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || null);
                    setSelectedClient(newValue);
                    trigger('clientId');
                  }}
                  value={selectedClient}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.clientId}
                      label={t('common.client')}
                      helperText={errors.clientId?.message}
                    />
                  )}
                  fullWidth
                />
              )}
            />
            <Button
              style={{ width: '30%', margin: '10px' }}
              onClick={() => setOpenAddClientModal(true)}
              variant="contained"
            >
              {t('common.add_new')}
            </Button>
          </Box>
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                sx={{ marginTop: 1 }}
                id="department-autocomplete"
                options={Array.isArray(jobDepartmentsResponse) ? jobDepartmentsResponse : []}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id || null);
                  setSelectedDepartment(newValue);
                  trigger('departmentId');
                }}
                value={selectedDepartment}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.departmentId}
                    label={t('common.department')}
                    helperText={errors.departmentId?.message}
                  />
                )}
                fullWidth
              />
            )}
          />
          <Controller
            name="jobReference"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                sx={{ marginTop: 1 }}
                id="job-reference-textfield"
                label={t('common.job_reference')}
                fullWidth
                variant="outlined"
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <TextField
            margin="dense"
            id="headcount"
            label={headcountLabel}
            type="number"
            fullWidth
            variant="outlined"
            defaultValue={0}
            {...register('headcount', {
              validate: (value) => value >= 0 || t('reports.headcount_error'),
            })}
            error={!!errors.headcount}
            helperText={errors.headcount?.message}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              const value = parseFloat(target.value);
              if (value < 0) {
                target.value = '0';
              }
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'start',
              width: '100%',
            }}
          >
            <FormControl sx={{ width: '50%', marginTop: 1 }}>
              <InputLabel id="demo-simple-select-helper-label">Seniority</InputLabel>
              <Select
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                defaultValue={'advanced'}
                label={seniorityLabel}
                {...register('seniority')}
                error={!!errors.seniority}
                onChange={handleChange}
              >
                <MenuItem value={'advanced'}>{seniorityAdvancedLabel}</MenuItem>
                <MenuItem value={'advanced_plus'}>{seniorityAdvancedPlusLabel}</MenuItem>
                <MenuItem value={'junior'}>{seniorityJuniorLabel}</MenuItem>
                <MenuItem value={'senior'}>{senioritySeniorLabel}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ marginLeft: 3, marginTop: 1 }}
              control={
                <Switch
                  color="primary"
                  checked={isConfidential}
                  onChange={(e) => setIsConfidential(e.target.checked)}
                />
              }
              label={t('form_fields.confidential.label')}
            />
          </div>
          <TextField
            margin="dense"
            id="description"
            label={descriptionLabel}
            type="text"
            fullWidth
            variant="outlined"
            {...register('description')}
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        </DialogContent>
        <DialogActionButtons
          onCancel={handleModalClose}
        />
      </form>
      {openAddClientModal && (
        <ClientFormDialog
          open={openAddClientModal}
          handleClose={() => setOpenAddClientModal(false)}
          onSuccess={handleAddNewClient}
        />
      )}
      {isLoading && <Loader isLoading={false} />}
    </FormDialogContainer>
  );
}